import { useState, useEffect, useRef } from 'react';

/**
 * Polls /api/version every 10 minutes and on tab/app focus.
 *
 * Jenn, 2026-09-05, after a real check-in silently failed to save: "we
 * can't tell our members to refresh. that's crazy. Other systems don't
 * need this" — right. A banner someone has to notice and click is not a
 * real fix for a stale build; a member has no reason to know that's even
 * the problem when something just quietly doesn't work.
 *
 * So this now auto-reloads on its own at the one moment that's actually
 * safe to: when the tab was hidden and is just now becoming visible again
 * (the ordinary "left it open, came back later" case this app runs into
 * constantly — a session sitting backgrounded across a deploy). At that
 * exact instant nothing has been typed into anything yet in this fresh
 * look at the page, so there's nothing to lose. Reloading the moment a
 * redeploy lands WHILE someone is actively mid-task (rarer — the 10-minute
 * background poll catching it) is the one case still worth a person's
 * say-so first, since a forced reload right then could drop something
 * genuinely unsaved — that's what the manual banner (still returned here)
 * is for, not the everyday case.
 */
export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const knownVersion = useRef(null);

  useEffect(() => {
    const check = async ({ autoReloadIfStale } = {}) => {
      try {
        const res = await fetch('/api/version', { cache: 'no-store' });
        if (!res.ok) return;
        const { version } = await res.json();
        if (knownVersion.current === null) {
          // First load — record the current version
          knownVersion.current = version;
        } else if (version !== knownVersion.current) {
          // Version changed — new deploy is live
          if (autoReloadIfStale) {
            window.location.reload();
          } else {
            setUpdateAvailable(true);
          }
        }
      } catch {
        // Network error — silently ignore, try again next interval
      }
    };

    check();

    // Re-check every 10 minutes — someone actively using the app when this
    // fires is the one case that still gets the manual banner, not a
    // reload out from under them mid-task.
    const interval = setInterval(check, 10 * 60 * 1000);

    // Coming back to a backgrounded tab/app is the safe moment to just
    // fix it — nothing's been touched yet in this fresh look at the page.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check({ autoReloadIfStale: true });
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return updateAvailable;
}
